<template>
  <Wrap v-if="faq.length">
    <div
      v-view-tracker="getParams()"
      class="main-space"
    >
      <ExpansionPanels
        class="frequently-asked-questions"
        item-scope
        item-type="https://schema.org/FAQPage"
      >
        <template #title>
          <h2 class="frequently-asked-questions__title">
            {{ title }}
          </h2>
        </template>

        <template #default>
          <template
            v-for="(el, i) in faq"
            :key="el.question"
          >
            <Item
              :open="openSection === i"
              @toggled="toggle(i, $event)"
              @click="sogu(openSection === i)"
            >
              <template #title>{{ el.question }}</template>
              <template #default>
                <span
                  v-if="el.answer"
                  v-html="el.answer"
                />

                <ul v-if="el.links">
                  <li
                    v-for="link in el.links"
                    :key="link.url"
                  >
                    <a :href="link.url">{{ link.text }}</a>
                  </li>
                </ul>
              </template>
            </Item>
          </template>
        </template>
      </ExpansionPanels>
    </div>
  </Wrap>
</template>

<script lang="ts" setup>
  import { toRaw } from 'vue';

  import Item from '@/features/frequentlyAskedQuestions/FrequentlyAskedQuestionItem.vue';
  import { trackEvent, useViewTracker } from '@/services/analytics';
  import Wrap from '@/shared/ContentWrap.vue';
  import { type FaqItem } from '@/shared/models/faq';
  import { useSeoStore } from '@/shared/store/seo';
  import { useExpansionPanelsController } from '@/shared/ui/expansionPanels/controller';
  import ExpansionPanels from '@/shared/ui/ExpansionPanels.vue';

  defineProps<{
    title: string;
    faq: FaqItem[];
  }>();

  const expansionPanelsController = useExpansionPanelsController();
  const { openSection } = expansionPanelsController;
  const toggle = (index: number, opened: boolean) => {
    expansionPanelsController.toggle(index, opened);
  };

  const vViewTracker = useViewTracker('sights:question.view');

  const seoStore = useSeoStore();

  const getParams = () => seoStore.geo;

  const sogu = (isClosed: boolean) => {
    if (!isClosed) {
      void trackEvent('sights:question.click', toRaw(seoStore.geo));
    }
  };
</script>

<style lang="scss" scoped>
  @use '@experience/shared/dist/assets/adaptivity' as legacy-adaptivity;

  .main-space {
    margin-top: var(--alias-space-80);

    @include legacy-adaptivity.tablet {
      margin-top: var(--alias-space-56);
    }

    @include legacy-adaptivity.mobile {
      margin-top: var(--alias-space-48);
    }
  }

  .frequently-asked-questions__title {
    font-size: inherit;
    font-style: inherit;
    font-weight: inherit;
    line-height: inherit;
  }

  a {
    color: var(--alias-color-link-default);
    text-decoration: none;
  }

  ul {
    padding: 0 24px;
    list-style: disc;

    li {
      margin: 4px 0 0;

      &:first-child {
        margin-top: 0;
      }
    }
  }
</style>
